import React, { useState, useRef, useCallback } from "react";
import "./FeedbackPage.css";

const BURST_MS = 2000;
const BURST_COUNT = 64;

function buildEmojiBurstPieces(emoji) {
  const ts = Date.now();
  return Array.from({ length: BURST_COUNT }, (_, i) => ({
    key: `${ts}-${i}`,
    emoji,
    x: Math.random() * 92 + 4,
    y: Math.random() * 85 + 5,
    dx: (Math.random() - 0.5) * 520,
    dy: (Math.random() - 0.5) * 600 + Math.random() * 120,
    rot: Math.random() * 1080 - 540,
    delay: Math.random() * 0.45,
    size: 26 + Math.floor(Math.random() * 34),
  }));
}

// ── Data ───────────────────────────────────────────────────────────────────────
const OPTIONS = [
  { id: "amazing", emoji: "🤩", label: "Amazingggg", sub: "We're so glad you loved it! 🚀" },
  { id: "loved",   emoji: "😍", label: "Loved it",   sub: "That made our day! ❤️" },
  { id: "fun",     emoji: "😊", label: "It was fun", sub: "Fun is what we aim for! 😄" },
  { id: "okay",    emoji: "👍", label: "It was okay", sub: "Thanks for being honest. We'll keep improving! 💪" },
];

// ── FeedbackCard ──────────────────────────────────────────────────────────────
function FeedbackCard({ option, selected, dimmed, onClick, animDelay }) {
  const emojiRef = useRef(null);

  const handleClick = () => {
    // Emoji pop
    if (emojiRef.current) {
      emojiRef.current.classList.remove("emoji--pop");
      void emojiRef.current.offsetWidth; // reflow
      emojiRef.current.classList.add("emoji--pop");
    }
    onClick(option);
  };

  return (
    <div
      className={[
        "fb-card",
        selected ? "fb-card--selected" : "",
        dimmed  ? "fb-card--dimmed"   : "",
      ].join(" ")}
      style={{ animationDelay: `${animDelay}s` }}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleClick()}
    >
      <span className="fb-card__emoji" ref={emojiRef}>{option.emoji}</span>
      <span className="fb-card__label">{option.label}</span>
    </div>
  );
}

// ── FeedbackPage (main) ───────────────────────────────────────────────────────
/**
 * Props:
 *   onSelect(label: string) — called when user confirms a rating
 */
export default function FeedbackPage({ onSelect }) {
  const [selected, setSelected]     = useState(null);
  const [locked, setLocked]         = useState(false);
  const [burstPieces, setBurstPieces] = useState(null);
  const [burstOn, setBurstOn] = useState(false);

  const handleCardClick = useCallback(
    (option) => {
      if (locked) return;
      if (selected?.id === option.id) return;
      setSelected(option);
      setLocked(true);
      const reduceMotion =
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!reduceMotion) {
        setBurstPieces(buildEmojiBurstPieces(option.emoji));
        setBurstOn(true);
      }
      window.setTimeout(() => {
        setBurstOn(false);
        setBurstPieces(null);
      }, BURST_MS);
      // Directly call onSelect after emoji burst
      setTimeout(() => {
        if (typeof onSelect === "function") Promise.resolve(onSelect(option.label));
      }, BURST_MS);
    },
    [selected, locked, onSelect]
  );

  return (
    <div className="fb-page">
      {/* Emoji burst overlay (selected option animation) */}
      {burstOn && burstPieces ? (
        <div className="fb-emoji-burst" aria-hidden="true">
          {burstPieces.map((p) => (
            <span
              key={p.key}
              className="fb-burst-emoji"
              style={{
                left: `${p.x}%`,
                top: `${p.y}%`,
                fontSize: `${p.size}px`,
                "--fb-dx": `${p.dx}px`,
                "--fb-dy": `${p.dy}px`,
                "--fb-rot": `${p.rot}deg`,
                animationDelay: `${p.delay}s`,
              }}
            >
              {p.emoji}
            </span>
          ))}
        </div>
      ) : null}

      {/* Background blobs */}
      <div className="fb-blob fb-blob--1" />
      <div className="fb-blob fb-blob--2" />

      {/* Content */}
      <div className="fb-content">
        <h1 className="fb-title">
          How much did you like<br />this experience?
        </h1>

        <div className="fb-grid">
          {OPTIONS.map((opt, i) => (
            <FeedbackCard
              key={opt.id}
              option={opt}
              selected={selected?.id === opt.id}
              dimmed={selected !== null && selected?.id !== opt.id}
              onClick={handleCardClick}
              animDelay={0.1 + i * 0.1}
            />
          ))}
        </div>

        <p className="fb-footer">Your feedback helps us improve!</p>
      </div>
    </div>
  );
}
